import type { useAuth } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import type { AppointmentPresenceHeartbeatRequest } from "../../../../shared/api-types";
import { ApiRequestError, NetworkRequestError, fetchWithAuthRetry } from "@/lib/api";
import { decideApiErrorCapture } from "@/lib/api-error-capture";
import { AppointmentPresenceHeartbeat } from "@/lib/appointment-presence";

type ClerkGetToken = ReturnType<typeof useAuth>["getToken"];

/**
 * The transport-failure report for beats that never reached the server — the
 * server can only log the failures it sees. Deliberately per-beat-silent:
 * the heartbeat controller invokes this once per consecutive-failure run
 * (first failure reports, a success re-arms), mirroring the Appointment Note
 * autosave hook's dedup, so a Redis or network outage never turns a
 * 15-second beat loop into Sentry spam. Tags only — never the error message.
 * A 401 that survived the retry is a sign-out, not a transport defect, so the
 * shared capture decision drops it and rates other 4xx as warnings.
 */
function capturePresenceHeartbeatFailure(error: unknown): void {
  const decision = decideApiErrorCapture(error);

  if (!decision.capture) return;

  const failure = classifyHeartbeatFailure(error);

  Sentry.captureException(new Error("appointment_presence_heartbeat_failed"), {
    level: decision.level,
    tags: {
      ...failure,
      operation: "heartbeat",
      source: "client",
      surface: "presence",
    },
  });
}

function classifyHeartbeatFailure(error: unknown): {
  code: "network_error" | "http_error" | "unexpected_response";
  status?: number;
} {
  if (error instanceof NetworkRequestError) return { code: "network_error" };

  if (error instanceof ApiRequestError) {
    return { code: "http_error", status: error.status };
  }

  return { code: "unexpected_response" };
}

/**
 * Marks this tab present on the Appointment documentation surface while it is
 * mounted, hidden or visible (NO-428: an open hidden tab counts as present, so
 * the automatic Enhancement waits for it). One browser-generated tab id per
 * mount keeps multiple tabs as independent leases; unmounting stops the beats,
 * after which the server-side lease expires on its own TTL.
 * A failed beat changes nothing for the RD and the loop retries on the next
 * interval; the first failure of each consecutive run is reported through the
 * client Sentry seam so a broken transport is still visible.
 */
export function useAppointmentPresenceHeartbeat({
  appointmentId,
  getToken,
}: {
  appointmentId: string;
  getToken: ClerkGetToken;
}): void {
  useEffect(() => {
    const tabId = crypto.randomUUID();
    const controller = new AppointmentPresenceHeartbeat(
      async () => {
        // The success answer is a bodyless 204, so the beat checks `ok`
        // itself: an error status must throw for the failure dedup to see it.
        const response = await fetchWithAuthRetry(
          getToken,
          `/api/appointments/${encodeURIComponent(appointmentId)}/presence`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              tabId,
            } satisfies AppointmentPresenceHeartbeatRequest),
          }
        );

        if (!response.ok) {
          throw new ApiRequestError(
            `Request failed with ${response.status}`,
            response.status,
            undefined
          );
        }
      },
      { onError: capturePresenceHeartbeatFailure }
    );
    controller.start();

    return () => {
      controller.dispose();
    };
  }, [appointmentId, getToken]);
}
